import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatNumber, plural } from '@/lib/format';
import { useIsAdmin } from '@/lib/hooks';
import { markAllNotificationsRead, useNotifications } from '@/lib/notifications';
import { openAdmin } from '@/lib/overlays';
import { goTo } from '@/lib/router';
import { signOut } from '@/lib/session';
import type { PaletteCommand } from '@/lib/slots';
import { openAccount } from './AccountDialog';
import { openShortcuts } from './Shortcuts';

// The palette commands the shell itself offers: going to each view, the bin, the admin console, notifications,
// shortcuts and your account. Areas add their own (writing an email, a new AI search, backing up)
// through registerCommands; the order numbers interleave them (see PaletteCommand.order in lib/slots.ts).

export interface SearchCounts {
  people: number;
  companies: number;
  deals: number;
}

/** How many records search covers. Shared by the palette footer and the "Go to" commands. */
export const searchCountsQuery = {
  queryKey: ['search-counts'],
  queryFn: () => api.get<SearchCounts>('/search/counts'),
  staleTime: 60_000,
} as const;

export function useCoreCommands(): PaletteCommand[] {
  const admin = useIsAdmin();
  const { data: counts } = useQuery(searchCountsQuery);
  const { unread } = useNotifications();

  const count = (n: number | undefined, one: string, many: string, fallback: string) =>
    n === undefined ? fallback : plural(n, one, many);

  const commands: PaletteCommand[] = [
    { id: 'go-today', title: 'Go to Home', sub: 'Your day at a glance', icon: 'home', keys: 'G T', keywords: 'today', jump: true, order: 10, run: () => goTo('today') },
    { id: 'go-inbox', title: 'Go to Inbox', sub: 'Tracked conversations and triage', icon: 'inbox', keys: 'G I', keywords: 'email mail', jump: true, order: 20, run: () => goTo('inbox') },
    { id: 'go-people', title: 'Go to People', sub: count(counts?.people, 'person', 'people', 'Everyone you work with'), icon: 'users', keys: 'G P', keywords: 'contacts', jump: true, order: 30, run: () => goTo('people') },
    { id: 'go-companies', title: 'Go to Companies', sub: count(counts?.companies, 'company', 'companies', 'Every organisation'), icon: 'building', keys: 'G C', keywords: 'organisations accounts', jump: true, order: 40, run: () => goTo('companies') },
    { id: 'go-deals', title: 'Go to Deals', sub: counts ? `Pipeline board · ${formatNumber(counts.deals)} open` : 'Pipeline board', icon: 'deals', keys: 'G D', keywords: 'pipeline board', jump: true, order: 50, run: () => goTo('deals') },
    { id: 'go-finder', title: 'Go to AI Finder', sub: 'Find companies and people', icon: 'sparkle', keys: 'G F', keywords: 'ai search leads', jump: true, order: 60, run: () => goTo('finder') },
    { id: 'go-activity', title: 'Go to Activity', sub: 'This week across the team', icon: 'activity', keys: 'G A', keywords: 'week team who', jump: true, order: 70, run: () => goTo('activity') },
    { id: 'go-bin', title: 'Open the Bin', sub: 'Restore anything for 30 days', icon: 'trash', keywords: 'deleted restore trash', order: 80, run: () => goTo('bin') },
  ];
  if (admin) {
    commands.push({ id: 'go-admin', title: 'Open the Admin console', sub: 'Backups, AI, members, inboxes', icon: 'shield', keywords: 'settings', order: 90, run: () => openAdmin() });
  }
  if (unread > 0) {
    commands.push({ id: 'notifications-read', title: 'Mark all notifications read', sub: 'Clears the bell badge', icon: 'check', keywords: 'bell', order: 150, run: () => void markAllNotificationsRead() });
  }
  commands.push(
    { id: 'account', title: 'Your account', sub: 'Name, email addresses and password', icon: 'user', keywords: 'profile password email', order: 165, run: () => openAccount() },
    { id: 'shortcuts', title: 'Keyboard shortcuts', sub: 'Every key in one sheet', icon: 'keyboard', keys: '?', keywords: 'keys help', order: 170, run: openShortcuts },
    { id: 'sign-out', title: 'Sign out', sub: 'End your session on this device', icon: 'logout', keywords: 'log out leave', order: 190, run: () => void signOut() },
  );
  return commands;
}
